/**
 * Answer a gap — the redesign's "Not in the files" check (DOP-623).
 *
 * Two answers, as choice cards:
 *  - "I know the answer": a value and where it came from. "Where it's from"
 *    is REQUIRED: a value a person typed is only as good as its provenance,
 *    and the memo prints that note as the value's source. An entered value
 *    shows as "entered by <name>" and never looks like an extracted figure.
 *  - "Ask the company" (`onAccept`): keep "Not found in provided materials"
 *    in the memo and the question in the diligence list; on a blocking field
 *    it also lifts the block.
 *
 * A block group ("transaction type OR raise size") is one gap per field
 * (DOP-643); the card says which other field's answer also lifts the block.
 * A card given several facts still lets the person say which one they answer.
 *
 * `editing` reopens an answer a person already saved (DOP-555), prefilled
 * with its value and source; saving replaces it.
 *
 * A figure needed for each year (`onFillEachYear`, DOP-650) cannot be
 * answered with one value: "I know the answer" opens the memo's financial
 * summary on its row, where each year is typed with where it came from.
 */

import React, { useId, useState } from "react";

import type {
	DealScreeningFact,
	DealScreeningSetFactPayload,
} from "../../types/dealScreening";
import { BTN, BTN_PRIMARY, INPUT, KICKER, KIND_KICKER } from "./checkStyles";
import { placeInFile } from "./ConflictChooser";
import { fromSavedSource } from "./documentSource";
import { plainMoney } from "./figuresEditModel";

export interface EnterWithSourceCardProps {
	/** The fact, or the members of a block group, the value can answer. */
	facts: DealScreeningFact[];
	blocking: boolean;
	saving: boolean;
	onSave: (fact: DealScreeningFact, payload: DealScreeningSetFactPayload) => void;
	/** "Ask the company": accept the gap as not found. Not offered without it. */
	onAccept?: (fact: DealScreeningFact) => void;
	/** Leave without answering; the button is not shown without it. */
	onCancel?: () => void;
	cancelLabel?: string;
	/** Reopen the person's saved answer to change it. */
	editing?: boolean;
	/** Where the memo writes its questions: "section 8" (DOP-573). */
	questionsIn: string;
	/** The other fields whose answer also lifts this block (DOP-643). */
	orAnswers?: string[];
	/** The figure needs a value per year: answer it in the financial summary (DOP-650). */
	onFillEachYear?: () => void;
}

export const UNIT_HINT: Record<string, string> = {
	usd_m: "USD millions, e.g. 8.0, $8M or $1.2bn",
	pct: "a percentage, e.g. 36% (or 0.36)",
	x: "a multiple, e.g. 4.2x",
	count: "a whole number, e.g. 1,200",
};

/** A money amount's scale word, as a multiple of a million (DOP-642). */
const MILLIONS: Record<string, number> = {
	"": 1,
	m: 1,
	mm: 1,
	mn: 1,
	mil: 1,
	million: 1,
	millions: 1,
	b: 1000,
	bn: 1000,
	billion: 1000,
	billions: 1000,
	k: 0.001,
	thousand: 0.001,
};

const CURRENCY = /^(usd|us\$|\$)/;

/**
 * The value as the API takes it: a number for numeric units, else the text.
 * A number is read the way people write one (DOP-642): "$25M", "25 million",
 * "$1.2bn", "$250k" and "(3.4)" for money in millions, "$100,000" for money
 * written out in dollars; "36%" or 0.36 for a
 * percentage; "4.2x" for a multiple; "1,200" for a count.
 */
export function parseEntry(raw: string, unit: string): number | string | null {
	const trimmed = raw.trim();
	if (trimmed === "") return null;
	if (unit === "text") return trimmed;
	let text = trimmed.toLowerCase().replace(/[,\s]/g, "").replace(CURRENCY, "");
	let sign = 1;
	if (/^\(.*\)$/.test(text)) {
		sign = -1;
		text = text.slice(1, -1);
	}
	if (text.startsWith("-")) {
		sign *= -1;
		text = text.slice(1);
	}
	text = text.replace(CURRENCY, "");
	const match = /^(\d*\.?\d+|\d+\.)([a-z%]*)$/.exec(text);
	if (!match) return null;
	const number = sign * Number(match[1]);
	const suffix = match[2];
	if (!Number.isFinite(number)) return null;
	if (unit === "usd_m") {
		const scale = MILLIONS[suffix];
		if (scale === undefined) return null;
		// With no unit word, how it is written says whether it is dollars or
		// $mm ("$100,000" is 0.1; "100" is 100): the edit-mode rule (DOP-660).
		return suffix === "" ? sign * plainMoney(match[1], raw.includes(",")) : number * scale;
	}
	if (unit === "pct") {
		if (suffix === "%") return number / 100;
		return suffix === "" ? number : null;
	}
	if (unit === "x") return suffix === "" || suffix === "x" ? number : null;
	return suffix === "" ? number : null;
}

const factName = (fact: DealScreeningFact): string =>
	fact.period ? `${fact.label} ${fact.period}` : fact.label;

/** "Transaction type", "Raise size" → "Transaction type or raise size". */
const groupName = (facts: DealScreeningFact[]): string =>
	facts
		.map((fact, index) =>
			index === 0 ? factName(fact) : factName(fact).charAt(0).toLowerCase() + factName(fact).slice(1),
		)
		.join(" or ");

const choiceCard = (selected: boolean) =>
	`flex w-full flex-col gap-3.5 rounded-xl bg-white text-left ${
		selected ? "border-2 border-[#004C4B] px-[15px] py-[13px]" : "border border-[#e5e7eb] px-4 py-3.5 hover:border-[#c9ccd1]"
	}`;

const RADIO = "mt-0.5 h-[18px] w-[18px] shrink-0 border-[#c9ccd1] text-[#004C4B] focus:ring-[#004C4B]";

const EnterWithSourceCard: React.FC<EnterWithSourceCardProps> = ({
	facts,
	blocking,
	saving,
	onSave,
	onAccept,
	onCancel,
	cancelLabel = "Cancel",
	editing = false,
	questionsIn,
	orAnswers = [],
	onFillEachYear,
}) => {
	const saved = editing && facts[0]?.state === "manual" ? facts[0] : null;
	const canAccept = Boolean(onAccept) && !editing;
	const [mode, setMode] = useState<"answer" | "accept">("answer");
	const [chosenIndex, setChosenIndex] = useState(0);
	const [value, setValue] = useState(
		saved && saved.value !== null ? String(saved.value) : "",
	);
	const [note, setNote] = useState(saved?.note ?? "");
	// An entry made from a place in a document (DOP-652) is sourced by that
	// link: reopened, it is kept, and a note is optional (DOP-664). Asking for
	// "where it's from" again left Save dead on a value that says where.
	const linked = saved ? fromSavedSource(saved.source) : null;
	const valueId = useId();
	const noteId = useId();
	const choiceName = useId();
	const fact = facts[chosenIndex] ?? facts[0];
	const parsed = parseEntry(value, fact.unit);
	const answering = mode === "answer";
	const eachYear = Boolean(onFillEachYear) && !editing;
	const sourced = note.trim() !== "" || linked !== null;
	const ready = !saving && (!answering || eachYear || (parsed !== null && sourced));
	// Why the answer cannot be saved yet, said rather than left to a greyed
	// button (DOP-642).
	const notANumber = value.trim() !== "" && parsed === null;
	const needsSource = answering && parsed !== null && !sourced;
	const kicker = editing ? null : KIND_KICKER[blocking ? "blocks" : "gap"];

	const submitLabel = saving
		? "Saving…"
		: editing
			? "Save changes"
			: answering && eachYear
				? "Fill in the Financial Summary"
				: answering
					? "Save and next"
					: blocking
						? "Accept and unblock the memo"
						: "Keep it as a question";

	const fields = eachYear ? (
		<p className="text-[13px] leading-normal text-[#3f4247]" data-testid="each-year-note">
			{fact.label} is needed for each year, so one value here can't answer it. The memo's
			Financial Summary opens on its row: type each year there, with where it came from, and
			save a new version.
		</p>
	) : (
		<div className="flex flex-col gap-3">
			{facts.length > 1 && (
				<fieldset>
					<legend className="text-[12.5px] font-semibold text-[#3f4247]">Which field</legend>
					<div className="mt-1.5 flex flex-wrap gap-x-5 gap-y-1">
						{facts.map((member, index) => (
							<label key={member.key} className="flex items-center gap-2 text-sm text-[#111111]">
								<input
									type="radio"
									name={`${choiceName}-field`}
									checked={chosenIndex === index}
									onChange={() => {
										setChosenIndex(index);
										setValue("");
									}}
									className={RADIO}
								/>
								{member.label}
							</label>
						))}
					</div>
				</fieldset>
			)}
			<div className="flex flex-col gap-1.5">
				<label htmlFor={valueId} className="text-[12.5px] font-semibold text-[#3f4247]">
					{fact.label}
					{fact.period ? ` · ${fact.period}` : ""}
				</label>
				{/* One box for every field, the same as "Where it's from" below:
				    a text answer and a figure read as the same kind of question
				    (DOP-643). A figure's box opens the number pad on a phone. */}
				<textarea
					id={valueId}
					value={value}
					onChange={(event) => setValue(event.target.value)}
					rows={2}
					inputMode={fact.unit === "text" ? undefined : "decimal"}
					className={INPUT}
				/>
				{notANumber ? (
					<p className="text-xs text-[#b54708]" role="alert">
						That isn't a number yet. Enter {UNIT_HINT[fact.unit] ?? "a number"}.
					</p>
				) : (
					UNIT_HINT[fact.unit] && <p className="text-xs text-[#6B7280]">{UNIT_HINT[fact.unit]}</p>
				)}
			</div>
			<div className="flex flex-col gap-1.5">
				<label htmlFor={noteId} className="text-[12.5px] font-semibold text-[#3f4247]">
					Where it's from
				</label>
				{/* A source is often more than a name and a date: who said it, on
				    what call, what document or thread it lives in. Room for that. */}
				<textarea
					id={noteId}
					value={note}
					onChange={(event) => setNote(event.target.value)}
					placeholder="e.g. Call with the CFO, Sep 22"
					rows={2}
					className={INPUT}
				/>
				{linked && saved?.source && (
					<p className="text-xs text-[#5B6472]" data-testid="entry-linked-source">
						Linked to {saved.source.title || saved.source.filename}
						{placeInFile(saved.source) ? `, ${placeInFile(saved.source)}` : ""}. The link is kept;
						a note is optional.
					</p>
				)}
				{needsSource && (
					<p className="text-xs text-[#b54708]">Add where it's from to save the answer.</p>
				)}
			</div>
		</div>
	);

	return (
		<form
			onSubmit={(event) => {
				event.preventDefault();
				if (!ready) return;
				if (answering && eachYear) onFillEachYear?.();
				else if (answering)
					onSave(fact, {
						value: parsed,
						note: note.trim(),
						period: fact.period,
						...(linked ? { source: linked } : {}),
					});
				else onAccept?.(fact);
			}}
			className="flex flex-col gap-4"
			aria-label="Enter a value with a source"
		>
			<div>
				{kicker && <p className={`${KICKER} ${kicker.color}`}>{kicker.label}</p>}
				<h2 className="mt-1 text-[22px] font-semibold leading-tight tracking-[-0.01em] text-[#111111]">
					{editing ? "Edit your answer" : groupName(facts)}
				</h2>
				<p className="mt-1 text-sm text-[#5B6472]">
					{editing
						? "Change the value or where it came from. The memo prints the new answer as entered by you, with the new source."
						: blocking
							? orAnswers.length > 0
								? `The memo can't be marked reviewed until this or ${orAnswers
										.map((label) => label.charAt(0).toLowerCase() + label.slice(1))
										.join(" or ")} is answered.`
								: "The memo can't be marked reviewed until this is answered."
							: "Doppler looked in the files and didn't find it."}
				</p>
			</div>

			{canAccept ? (
				<div role="radiogroup" aria-label="What to do" className="flex flex-col gap-2.5">
					<div className={choiceCard(answering)}>
						<label className="flex cursor-pointer gap-3">
							<input
								type="radio"
								name={choiceName}
								checked={answering}
								onChange={() => setMode("answer")}
								className={RADIO}
							/>
							<span>
								<span className="block text-sm font-semibold text-[#111111]">I know the answer</span>
								<span className="mt-0.5 block text-[12.5px] leading-snug text-[#5B6472]">
									It goes in the memo marked as entered by you, with where you heard it.
								</span>
							</span>
						</label>
						{answering && <div className="pl-[30px]">{fields}</div>}
					</div>
					<div className={choiceCard(!answering)}>
						<label className="flex cursor-pointer gap-3">
							<input
								type="radio"
								name={choiceName}
								checked={!answering}
								onChange={() => setMode("accept")}
								className={RADIO}
							/>
							<span>
								<span className="block text-sm font-semibold text-[#111111]">Ask the company</span>
								<span className="mt-0.5 block text-[12.5px] leading-snug text-[#5B6472]">
									The memo keeps “Not found in provided materials” and the question stays in{" "}
									{questionsIn}.{blocking ? " The block is lifted." : ""}
								</span>
							</span>
						</label>
					</div>
				</div>
			) : (
				<div className="rounded-xl border border-[#e5e7eb] bg-white px-4 py-3.5">{fields}</div>
			)}

			<div className="flex flex-wrap items-center gap-2.5">
				{onCancel && (
					<button type="button" onClick={onCancel} disabled={saving} className={BTN}>
						{cancelLabel}
					</button>
				)}
				<button type="submit" disabled={!ready} className={`${BTN_PRIMARY} ml-auto`}>
					{submitLabel}
				</button>
			</div>
		</form>
	);
};

export default EnterWithSourceCard;
